/**
 * Which failures may be parked for replay, and which must never be (SE-6983).
 *
 * This is the single most consequential rule in the offline path, because both
 * mistakes are silent.
 *
 * **Queue a rejection the server actually made**, and it replays forever: the
 * scout is told their work is safe and will sync, the drain re-sends it on
 * every reconnect, and the server refuses it every time. Nothing surfaces,
 * because a parked write reports "will sync" by construction.
 *
 * **Refuse to queue a transport failure**, and the scout at a ground with no
 * signal is told "Not saved" for work that could have been kept — which is the
 * failure this whole ticket exists to remove.
 *
 * So the discriminator has to be exact: only a failure that means *the request
 * never reached a server that could judge it* is queueable.
 *
 * ## Why `graphQLErrors` disqualifies even when a `networkError` is present
 *
 * Apollo can report both at once — a response that arrived, was understood,
 * and carried errors, over a connection that then broke. If the server judged
 * the operation at all, replaying it changes nothing about the verdict. The
 * presence of any `graphQLErrors` is therefore decisive, and it is checked
 * first.
 *
 * `FEATURE_DISABLED` is the case that makes this concrete: the match-report
 * mutations refuse with it when the server-side flag is off, and a build that
 * queued those would fill a scout's device with writes that can never land.
 * @module features/match-report-capture/utils/isQueueableFailure
 */

/** The parts of an Apollo error this rule reads. */
interface ApolloShapedError {
  /** Errors the server returned in the response body. */
  readonly graphQLErrors?: readonly unknown[];
  /** A transport-level failure, when one occurred. */
  readonly networkError?: unknown;
}

/**
 * Whether a failed write may be parked on the device for replay.
 * @param error - Whatever the write threw.
 * @returns True only for a transport failure the server never judged.
 */
export const isQueueableFailure = (error: unknown): boolean => {
  if (error === null || typeof error !== "object") return false;

  const candidate = error as ApolloShapedError;

  // The server spoke. Whatever it said, saying it again will not change it.
  if (
    Array.isArray(candidate.graphQLErrors) &&
    candidate.graphQLErrors.length > 0
  ) {
    return false;
  }

  return (
    candidate.networkError !== undefined && candidate.networkError !== null
  );
};
